/**
 * Microsoft Integrations Settings — Teams webhook, flag routing email, bot status
 * PRD §5.2 — admin onboarding for Andon delivery
 */
import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { officeService, OfficeSettingsData } from '../../../services/api/officeService';
import { Spiner } from '../../../components';

interface Props {
    accessToken: string;
}

const MicrosoftIntegrationsSettings: React.FC<Props> = ({ accessToken }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [settings, setSettings] = useState<OfficeSettingsData | null>(null);
    const [webhookUrl, setWebhookUrl] = useState('');
    const [routingEmail, setRoutingEmail] = useState('');
    const [botConnected, setBotConnected] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await officeService.getOfficeSettings(accessToken);
            setSettings(data);
            setWebhookUrl(data.teams_webhook_url || '');
            setRoutingEmail(data.flag_routing_email || '');
            setBotConnected(Boolean(data.bot_connected));

            try {
                const status = await officeService.getBotStatus(accessToken);
                setBotConnected(Boolean(status.connected));
            } catch {
                /* settings.bot_connected already set */
            }
        } catch (err: any) {
            toast.error(err?.message || t('integrations.loadError', 'Failed to load Microsoft settings'));
        } finally {
            setLoading(false);
        }
    }, [accessToken, t]);

    useEffect(() => {
        void load();
    }, [load]);

    const handleSave = async () => {
        setSaving(true);
        try {
            const data = await officeService.updateOfficeSettings(accessToken, {
                teams_webhook_url: webhookUrl.trim() || null,
                flag_routing_email: routingEmail.trim() || null,
            });
            setSettings(data);
            toast.success(t('integrations.saved', 'Microsoft integration settings saved'));
        } catch (err: any) {
            toast.error(err?.message || t('integrations.saveError', 'Failed to save settings'));
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="p-4">
                <Spiner className="d-flex justify-content-center spinner-border-sm" />
            </div>
        );
    }

    return (
        <div className="microsoft-integrations p-4" style={{ maxWidth: 720 }}>
            <h2 className="h4 mb-2">
                {t('integrations.title', 'Microsoft Integrations')}
            </h2>
            <p className="text-muted mb-4">
                {t(
                    'integrations.subtitle',
                    'Configure Andon routing to Teams and email for your company admins.'
                )}
            </p>

            <div
                className="mb-4 p-3 rounded"
                style={{
                    background: botConnected ? 'rgba(34,197,94,0.12)' : 'rgba(234,179,8,0.12)',
                    border: `1px solid ${botConnected ? 'rgba(34,197,94,0.35)' : 'rgba(234,179,8,0.35)'}`,
                }}
            >
                <strong>
                    {botConnected
                        ? t('integrations.botConnected', 'Famla Teams bot connected')
                        : t('integrations.botDisconnected', 'Famla Teams bot not connected yet')}
                </strong>
                <p className="mb-2 mt-2 small">
                    {t(
                        'integrations.botHint',
                        'Open the Famla app in Microsoft Teams once so we can deliver Andon cards and discovery questions to your DM.'
                    )}
                </p>
                <a
                    className="btn btn-sm btn-outline-primary"
                    href="https://teams.microsoft.com/l/app/ffbf34bc-3bb1-4d06-b7c2-f82e044808df"
                    target="_blank"
                    rel="noreferrer"
                >
                    {t('integrations.openTeams', 'Open Famla in Teams')}
                </a>
            </div>

            <div className="mb-3">
                <label className="form-label" htmlFor="flag-routing-email">
                    {t('integrations.flagEmail', 'Flag routing email (optional override)')}
                </label>
                <input
                    id="flag-routing-email"
                    className="form-control"
                    type="email"
                    value={routingEmail}
                    onChange={(e) => setRoutingEmail(e.target.value)}
                    placeholder="ops@company.com"
                />
                <div className="form-text">
                    {t(
                        'integrations.flagEmailHelp',
                        'By default Andon emails go to all company admins. Set a routing address only if you need an Ops alias.'
                    )}
                </div>
            </div>

            <div className="mb-4">
                <label className="form-label" htmlFor="teams-webhook">
                    {t('integrations.teamsWebhook', 'Teams channel webhook (optional Ops fallback)')}
                </label>
                <input
                    id="teams-webhook"
                    className="form-control"
                    type="url"
                    value={webhookUrl}
                    onChange={(e) => setWebhookUrl(e.target.value)}
                    placeholder="https://outlook.office.com/webhook/..."
                />
                <div className="form-text">
                    {t(
                        'integrations.teamsWebhookHelp',
                        'Primary delivery is a 1:1 DM via the Famla bot. A channel webhook is only an optional Ops backup.'
                    )}
                </div>
            </div>

            <button
                type="button"
                className="btn btn-primary"
                onClick={handleSave}
                disabled={saving}
            >
                {saving
                    ? t('integrations.saving', 'Saving…')
                    : t('integrations.save', 'Save settings')}
            </button>

            {settings?.company_id && (
                <p className="text-muted small mt-3 mb-0">
                    Company: {settings.company_id}
                </p>
            )}
        </div>
    );
};

export default MicrosoftIntegrationsSettings;
